<template>
  <div
    id="progressDialog"
    class="text-xs-center"
    v-if="currentMission != null && visibility === true"
  >
    <v-dialog
      v-model="localVarComputing"
      hide-overlay
      persistent
      :width="this.currentMission.typeName === 'SimulationRequest' ? 900 : 300"
    >
      <template v-slot:activator="{ on }">
        <v-btn
          :disabled="workInProgress"
          :loading="workInProgress"
          class="white--text"
          :color="buttonColor"
          v-on="on"
          @click.stop="compute()"
        >
          {{ text }}
        </v-btn>
      </template>

      <v-card color="primary" dark>
        <v-card-text>
          Please stand by {{ computationInfo }}
          <v-progress-linear
            v-model="missionStatus"
            color="white"
            class="mb-0 black--text font-weight-light caption"
            height="15px"
          >
            <div class="w-100 text-center">
              {{ missionStatus !== -1 ? missionStatus : 100 }}
            </div>
          </v-progress-linear>
        </v-card-text>
        <v-card-actions class="justify-center p-3">
          <v-btn light depressed small @click.stop="cancelCompute()"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-card>
      <div id="livePlotMap"></div>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import dashboardTools from '../../tools/dashboard.js'
import logHandler from '../../mixins/logHandler'

export default {
  name: 'progress-dialog',
  props: {
    computing: Boolean,
    workInProgress: Boolean,
    callbackStatus: {
      type: Function,
      default: () => {}
    },
    buttonColor: {
      type: String,
      required: true
    },
    text: {
      type: String,
      required: true
    },
    visibility: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      interval: null,
      intervalMission: null,
      lastStopDate: 0,
      delayBeforeRestartingMS: 1500,
      initialDelayMS: 1000,
      refreshDelayMS: 500,
      statusErrorCounter: 0,
      missionStatus: -1,
      missionError: null,
      localVarComputing: this.computing // avoid error of updating computing by side effect on v-model
    }
  },
  mixins: [logHandler],

  mounted() {
    this.$root.$on('current-results-loading', this.currentResultsLoadingHandler)
    this.$root.$on('current-results-updated', this.currentResultsUpdatedHandler)
    this.statusErrorCounter = 0
    this.checkMissionStatus()
  },

  beforeDestroy() {
    this.stopRefreshThread()
    this.$root.$off(
      'current-results-loading',
      this.currentResultsLoadingHandler
    )
    this.$root.$off(
      'current-results-updated',
      this.currentResultsUpdatedHandler
    )
  },

  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
      currentMission: 'currentMission',
      currentResultsLog: 'currentResultsLog'
    }),

    computationInfo() {
      if (this.missionStatus === 0) {
        return '(process pending ...)'
      }
      if (this.missionStatus <= 5 && this.missionStatus > 0) {
        return '(initializing data ...)'
      }
      if (this.missionStatus >= 95) {
        return '(preparing results ...)'
      }
      if (this.missionStatus > 5) {
        return '(computing ...)'
      }
      return ''
    }
  },
  methods: {
    currentResultsLoadingHandler() {
      this.setComputing(false)
      this.setWorkInProgress(true)
    },
    currentResultsUpdatedHandler() {
      this.setComputing(false)
      this.setWorkInProgress(false)
    },

    stopRefreshThread() {
      if (this.interval) {
        this.lastStopDate = Date.now()
        clearInterval(this.interval)
        this.interval = null
        // do not set intervalMission to null, keep a trace to not restart immediately
      }
    },

    setComputing(value) {
      this.localVarComputing = value
      this.$emit('update:computing', value)
    },
    setWorkInProgress(value) {
      this.$emit('update:workInProgress', value)
    },

    startRefreshStatusThread() {
      let delaySinceStop = this.delayBeforeRestartingMS + 1
      if (this.intervalMission === this.currentMission.id) {
        if (this.interval != null) {
          this.stopRefreshThread()
        } else if (
          this.lastStopDate !== 0 &&
          this.intervalMission === this.currentMission.id
        ) {
          // detect weird infinite refresh loop
          delaySinceStop = Date.now() - this.lastStopDate
        }
      } else {
        this.stopRefreshThread() // reset
      }
      if (delaySinceStop > this.delayBeforeRestartingMS) {
        this.intervalMission = this.currentMission.id
        this.setComputingState()
        this.sleep(this.initialDelayMS).then(() => {
          this.interval = setInterval(
            function () {
              this.getMissionStatus()
            }.bind(this),
            this.refreshDelayMS
          ) // bind important to call other methods*/
        })
      }
    },

    setComputingState() {
      this.stopRefreshThread()
      if (this.currentResultsLog != null) {
        this.currentResultsLog.results = null
      }
      this.setComputing(true)
      this.setWorkInProgress(true)
    },

    async sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms))
    },

    compute() {
      this.missionStatus = 0
      this.missionError = null
      this.setComputingState()
      this.lastStopDate = 0
      this.intervalMission = null
      // start computation
      this.$http
        .get(this.getCurrentAPI() + '/compute/' + this.currentMission.id)
        .then(() => {
          this.startRefreshStatusThread()
        })
        .catch((e) => {
          this.missionError = e
          this.missionStatus = -1
          this.displayMessage(
            'Error computing ',
            e,
            'notification',
            'error',
            this
          )
          this.stopRefreshThread()
        })
    },

    getMissionStatus() {
      if (this.currentMission != null) {
        this.$http
          .get(this.getCurrentAPI() + '/status/' + this.currentMission.id)
          .then((response) => {
            this.statusErrorCounter = 0
            this.missionStatus = response.data
            if (this.missionStatus === -1) {
              this.stopRefreshThread()
              this.setComputing(false)
              // computation ended : reload the object but keep work in progress flag until result loaded
              this.$http
                .get(this.getCurrentAPI() + '/' + this.currentMission.id)
                .then((response) => {
                  this.$root.$emit('set-current-mission', response.data)
                })
                .catch((e) => {
                  this.displayMessage(
                    'Error loading ',
                    e,
                    'notification',
                    'error',
                    this
                  )
                })
            } else {
              if (!this.localVarComputing) {
                this.setComputing(true)
              }
            }
          })
          .catch((e) => {
            this.statusErrorCounter = this.statusErrorCounter + 1
            if (this.statusErrorCounter > 120) {
              // 2min of errors => stop
              this.stopRefreshThread()
              this.statusErrorCounter = 0
              this.missionStatus = -1
              this.missionError = e
              this.displayMessage('Error loading ', e, 'notification', 'error')
            }
          })
        this.callbackStatus()
      }
    },

    checkMissionStatus() {
      if (this.currentMission != null) {
        if (
          this.currentResultsLog == null ||
          this.currentResultsLog.results == null
        ) {
          this.$http
            .get(this.getCurrentAPI() + '/status/' + this.currentMission.id)
            .then((response) => {
              if (response.data !== -1) {
                this.missionStatus = response.data
                this.startRefreshStatusThread()
              }
            })
            .catch((e) => {
              this.stopRefreshThread()
              this.missionError = e
              this.missionStatus = -1
            })
        }
      }
    },

    cancelCompute() {
      this.stopRefreshThread()
      this.setComputing(false)
      this.setWorkInProgress(false)
      if (this.currentResultsLog != null) {
        this.currentResultsLog.results = null
      }
      this.missionStatus = -1
      this.$http
        .get(this.getCurrentAPI() + '/stop/' + this.currentMission.id)
        .then(() => {})
        .catch((e) => {
          this.missionError = e
        })
    },

    getCurrentAPI() {
      return dashboardTools.getCurrentAPI(this)
    }
  }
}
</script>

<style scoped></style>
